import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Section, Cell, Caption } from '@telegram-apps/telegram-ui'
import { CalendarDays, AlertTriangle, Target, ClipboardCheck, Car, Plus } from 'lucide-react'

import { api } from '../../lib/api'
import type { Task, CalendarEvent, Me } from '../../lib/types'
import { formatDateTime, formatDateLong } from '../../lib/format'
import { SkeletonList } from '../../components/Skeleton'
import PageHeader from '../../components/PageHeader'
import TaskCard from '../../components/TaskCard'

interface DashboardData {
  today_events: CalendarEvent[]
  my_active_tasks: Task[]
  secretary_active_tasks: Task[]
  driver_active_tasks: Task[]
  overdue_secretary: Task[]
  overdue_driver: Task[]
}

export default function DirectorDashboard() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const me = qc.getQueryData<Me>(['me'])

  const { data, isLoading } = useQuery<DashboardData>({
    queryKey: ['dashboard'],
    queryFn: () => api.get<DashboardData>('/dashboard'),
  })

  const markDone = useMutation({
    mutationFn: (taskId: number) => api.post(`/tasks/${taskId}/done`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      qc.invalidateQueries({ queryKey: ['tasks'] })
    },
  })

  const today = new Date()
  const greeting = today.getHours() < 12 ? 'Доброе утро' : today.getHours() < 18 ? 'Добрый день' : 'Добрый вечер'
  const dirName = (me?.director_name || me?.name || '').trim()
  const secName = me?.secretary_name || 'Анастасия'
  const drvName = me?.driver_name || 'Владимир'

  if (isLoading || !data) {
    return (
      <>
        <PageHeader title={`${greeting}${dirName ? ', ' + dirName : ''}`} caption={formatDateLong(today)} />
        <SkeletonList count={6} />
      </>
    )
  }

  const overdueTotal = data.overdue_secretary.length + data.overdue_driver.length

  return (
    <>
      <PageHeader title={`${greeting}${dirName ? ', ' + dirName : ''}`} caption={formatDateLong(today)} />

      {/* KPI strip */}
      <div className="kpi-row">
        <KpiCard value={data.my_active_tasks.length} label="У меня" tone="default" />
        <KpiCard value={data.secretary_active_tasks.length} label={secName} tone="default" />
        <KpiCard value={data.driver_active_tasks.length} label={drvName} tone="default" />
        <KpiCard value={overdueTotal} label="Просрочено" tone={overdueTotal > 0 ? 'danger' : 'default'} />
      </div>

      <Section header={<span className="section-label" style={{padding:0}}><CalendarDays/>Сегодня</span>}>
        {data.today_events.length === 0 ? (
          <Cell><Caption style={{ opacity: 0.6 }}>Сегодня свободно</Caption></Cell>
        ) : (
          data.today_events.map((e, i) => (
            <Cell
              key={i}
              before={<strong style={{ fontVariantNumeric: 'tabular-nums' }}>{formatDateTime(e.start)}</strong>}
              multiline
            >
              {e.summary}
            </Cell>
          ))
        )}
      </Section>

      {data.overdue_secretary.length > 0 && (
        <Section header={<span className="section-label" style={{padding:0, color:'var(--danger)'}}><AlertTriangle/>Просрочено у {secName}</span>}>
          {data.overdue_secretary.map((t) => (
            <TaskCard key={t.id} task={t} canCheck={false} detailPath={`/tasks/${t.id}`} />
          ))}
        </Section>
      )}

      {data.overdue_driver.length > 0 && (
        <Section header={<span className="section-label" style={{padding:0, color:'var(--danger)'}}><AlertTriangle/>Просрочено у {drvName}</span>}>
          {data.overdue_driver.map((t) => (
            <TaskCard key={t.id} task={t} canCheck={false} detailPath={`/tasks/${t.id}`} />
          ))}
        </Section>
      )}

      <Section header={<span className="section-label" style={{padding:0}}><Target/>Мои ближайшие задачи</span>}>
        {data.my_active_tasks.length === 0 ? (
          <Cell><Caption style={{ opacity: 0.6 }}>Чисто</Caption></Cell>
        ) : (
          data.my_active_tasks.map((t) => (
            <TaskCard
              key={t.id}
              task={t}
              canCheck={true}
              onToggleDone={(id) => markDone.mutate(id)}
              detailPath={`/tasks/${t.id}`}
            />
          ))
        )}
      </Section>

      <Section header={<span className="section-label" style={{padding:0}}><ClipboardCheck/>У {secName} в работе ({data.secretary_active_tasks.length})</span>}>
        {data.secretary_active_tasks.length === 0 ? (
          <Cell><Caption style={{ opacity: 0.6 }}>Нет активных поручений</Caption></Cell>
        ) : (
          data.secretary_active_tasks.map((t) => (
            <TaskCard key={t.id} task={t} canCheck={false} detailPath={`/tasks/${t.id}`} />
          ))
        )}
      </Section>

      <Section header={<span className="section-label" style={{padding:0}}><Car/>У {drvName} в работе ({data.driver_active_tasks.length})</span>}>
        {data.driver_active_tasks.length === 0 ? (
          <Cell><Caption style={{ opacity: 0.6 }}>Нет активных поручений</Caption></Cell>
        ) : (
          data.driver_active_tasks.map((t) => (
            <TaskCard key={t.id} task={t} canCheck={false} detailPath={`/tasks/${t.id}`} />
          ))
        )}
      </Section>

      <button className="btn-primary" onClick={() => navigate('/tasks/new')}>
        <Plus size={18} strokeWidth={2.4} />
        Создать задачу
      </button>
    </>
  )
}

function KpiCard({ value, label, tone }: { value: number; label: string; tone: 'default' | 'danger' }) {
  return (
    <div className={`kpi-card${tone === 'danger' ? ' kpi-danger' : ''}`}>
      <div className="kpi-value">{value}</div>
      <div className="kpi-label">{label}</div>
    </div>
  )
}
